import React, { useState, useEffect } from 'react';
import { workCategories } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Ambulance, Heart, Home, Users, Shield, AlertCircle, Scissors, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import contentData from '../data/content.json';

const iconMap = {
  'Ambulance': Ambulance,
  'Heart': Heart,
  'Home': Home,
  'Users': Users,
  'Shield': Shield,
  'AlertCircle': AlertCircle,
  'Scissors': Scissors
};

// Helper function to get images array (handles both string and array)
const getImages = (image) => Array.isArray(image) ? image : [image];

// Success stories from content.json
const successStories = contentData.featuredStories.map(story => ({
  id: story.id,
  title: story.title,
  subtitle: story.subtitle,
  story: story.description,
  image: story.image
}));

// Auto-scrolling Story Card component
const StoryCard = ({ story, onReadMore }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = getImages(story.image);

  useEffect(() => {
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3000); // Auto-scroll every 3 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-300">
      {/* Big Centered Image with Auto-scroll */}
      <div className="relative h-[280px] overflow-hidden">
        {/* Blurred background to fill empty space */}
        <img
          src={images[currentIndex]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60"
        />
        {/* Main image */}
        <img
          src={images[currentIndex]}
          alt={story.title}
          className="relative w-full h-full object-contain"
        />
        {/* Dot indicators for cards with multiple images */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, index) => (
              <span
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${index === currentIndex ? 'bg-white' : 'bg-white/50'
                  }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-6">
        <h3 className="text-xl font-bold font-heading text-forest-green mb-2">
          {story.title}
        </h3>
        <p className="text-warm-amber font-semibold text-sm mb-3">{story.subtitle}</p>

        {/* Truncated Paragraph */}
        <p className="text-gray-700 leading-relaxed line-clamp-3 mb-4">
          {story.story}
        </p>

        {/* Read More Button */}
        <button
          onClick={() => onReadMore(story)}
          className="inline-flex items-center text-forest-green font-semibold hover:text-warm-amber transition-colors duration-200"
        >
          Read More
          <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const OurWork = () => {
  const [selectedStory, setSelectedStory] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Auto-scroll for modal images
  useEffect(() => {
    if (!selectedStory) return;

    const images = getImages(selectedStory.image);
    if (images.length <= 1) return;

    const interval = setInterval(() => {
      setModalImageIndex((prev) => (prev + 1) % images.length);
    }, 4000); // Auto-scroll every 4 seconds in modal

    return () => clearInterval(interval);
  }, [selectedStory]);

  // Handle opening modal with reset image index
  const openStoryModal = (story) => {
    setSelectedStory(story);
    setModalImageIndex(0);
  };

  const collageImages = [...contentData.featuredStories, ...contentData.stories]
    .map((story) => ({
      src: getImages(story.image)[0],
      title: story.title
    }))
    .filter((story, index, collection) => (
      collection.findIndex((item) => item.src === story.src) === index
    ));

  return (
    <div className="min-h-screen">
      {/* Hero Section — Story Collage */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* Collage mosaic background */}
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-4 gap-0.5 md:grid-cols-5 md:grid-rows-2">
          {collageImages.slice(0, 10).map((item, i) => (
            <div key={i} className="relative overflow-hidden">
              <img
                src={item.src}
                alt={item.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/10" />
            </div>
          ))}
        </div>
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/85 to-forest-green/70 z-10"></div>
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-4">
            Our Work
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Rescue, Rehabilitate, and Reimagine a Kinder World
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              At Animal Care India, we are driven by empathy to ensure the safety and well-being of every animal in need. Our rescue operations, rehabilitation efforts, and safe shelters provide comfort to the most vulnerable. Through education and advocacy, we inspire compassion, fostering a deeper understanding of animal rights within communities.
            </p>
          </div>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Success Stories
            </h2>
            <p className="text-xl text-gray-700">
              Rescue & Rehabilitation - We provide immediate medical care and shelter for animals in distress.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {successStories.map((story) => (
              <StoryCard
                key={story.id}
                story={story}
                onReadMore={openStoryModal}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Story Dialog Modal */}
      {selectedStory && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStory(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <div className="sticky top-0 flex justify-end p-4 bg-white/90 backdrop-blur-sm">
              <button
                onClick={() => setSelectedStory(null)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {/* Modal Image Gallery */}
            <div className="px-6">
              <div className="relative h-[350px] rounded-2xl overflow-hidden">
                {/* Blurred background to fill empty space */}
                <img
                  src={getImages(selectedStory.image)[modalImageIndex]}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60"
                />
                {/* Main image */}
                <img
                  src={getImages(selectedStory.image)[modalImageIndex]}
                  alt={selectedStory.title}
                  className="relative w-full h-full object-contain"
                />

                {/* Navigation Arrows - only show if multiple images */}
                {getImages(selectedStory.image).length > 1 && (
                  <>
                    <button
                      onClick={() => setModalImageIndex((prev) =>
                        prev === 0 ? getImages(selectedStory.image).length - 1 : prev - 1
                      )}
                      className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors duration-200"
                    >
                      <ChevronLeft className="w-5 h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={() => setModalImageIndex((prev) =>
                        prev === getImages(selectedStory.image).length - 1 ? 0 : prev + 1
                      )}
                      className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg transition-colors duration-200"
                    >
                      <ChevronRight className="w-5 h-5 text-gray-700" />
                    </button>
                  </>
                )}
              </div>

              {/* Dot Indicators - only show if multiple images */}
              {getImages(selectedStory.image).length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {getImages(selectedStory.image).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setModalImageIndex(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-colors duration-200 ${index === modalImageIndex
                        ? 'bg-forest-green'
                        : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <h3 className="text-2xl font-bold font-heading text-forest-green mb-2">
                {selectedStory.title}
              </h3>
              <p className="text-warm-amber font-semibold mb-4">{selectedStory.subtitle}</p>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedStory.story}
              </p>
            </div>
          </div>
        </div>
      )}



      {/* Our Services / Work Categories */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              What We Do
            </h2>
            <p className="text-xl text-gray-700">
              Comprehensive care from rescue to rehabilitation
            </p>
          </div>
          <div className="space-y-20">
            {workCategories.map((category, index) => {
              const Icon = iconMap[category.icon];
              const isEven = index % 2 === 0;

              return (
                <div key={category.id} className="max-w-7xl mx-auto">
                  <div className={`grid md:grid-cols-2 gap-12 items-center ${!isEven ? 'md:grid-flow-dense' : ''}`}>
                    <div className={`relative h-[400px] rounded-2xl overflow-hidden shadow-2xl ${!isEven ? 'md:col-start-2' : ''}`}>
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-forest-green/60 to-transparent"></div>
                    </div>
                    <div className={!isEven ? 'md:col-start-1 md:row-start-1' : ''}>
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-14 h-14 bg-warm-amber rounded-full flex items-center justify-center">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-warm-amber font-semibold text-sm uppercase tracking-wide">
                          {category.stats}
                        </span>
                      </div>
                      <h2 className="text-4xl font-bold font-heading text-forest-green mb-6">
                        {category.title}
                      </h2>
                      <p className="text-lg text-gray-700 leading-relaxed mb-6">
                        {category.description}
                      </p>

                      {/* Additional details based on category */}
                      {category.id === 1 && (
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>24/7 emergency helpline for injured animals</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Rapid response team with equipped ambulances</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>On-site first aid and immediate care</span>
                          </li>
                        </ul>
                      )}

                      {category.id === 2 && (
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Surgical sterilization (spay/neuter) by qualified veterinarians</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Prevention of unwanted litters and reduction of street overpopulation</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Post-operative care and safe return to habitat</span>
                          </li>
                        </ul>
                      )}

                      {category.id === 5 && (
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Reflective collar distribution in high-risk zones</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Mass vaccination and deworming drives</span>
                          </li>
                          <li className="flex items-start">
                            <span className="w-2 h-2 bg-warm-amber rounded-full mt-2 mr-3"></span>
                            <span>Community education programs</span>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-8">
              Our Commitment
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              We are committed to rescuing, rehabilitating, and protecting animals in distress, ensuring they receive the care they deserve. Our efforts include swift emergency rescues, life-saving medical treatment, and long-term rehabilitation to give animals a second chance at life. Beyond immediate aid, we focus on raising public awareness to foster a compassionate society where animals are free from abuse, neglect, and suffering. Through education and outreach, we inspire communities to take responsibility for animal welfare and promote lasting change. Together, we can create a safe, loving environment where all animals thrive and live with dignity and care.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <img
          src="/team/gallery4.jpg"
          alt="Join Us"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold font-heading mb-6">
            Join Us in Making a Difference
          </h2>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Every contribution, big or small, helps us save more lives and create a kinder world for animals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/donate"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-warm-amber hover:bg-amber-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-colors duration-200 shadow-lg"
            >
              Donate Now
            </Link>
            <Link
              to="/volunteer"
              className="bg-white text-forest-green hover:bg-gray-100 px-8 py-4 rounded-full font-bold text-lg transition-colors duration-200 shadow-lg"
            >
              Volunteer With Us
            </Link>
          </div>
        </div>
      </section>
    </div >
  );
};

export default OurWork;